import { Component } from "react";
import { MOOD_SELECT_CLOSE_DELAY_MS } from "../constants";
import { getPopularMoods } from "../shuffle-moods";
import { recordMoodUsed } from "../slowed-memory";

function SparkleIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6L4.5 10l5.6-1.9L12 2.5z"
        fill="currentColor"
      />
      <path
        d="M18.5 14.5l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z"
        fill="currentColor"
        opacity="0.7"
      />
      <path
        d="M5 15l.6 1.7 1.7.6-1.7.6L5 19.6l-.6-1.7-1.7-.6 1.7-.6L5 15z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  );
}

export default class Mood extends Component {
  constructor(props) {
    super(props);
    this.chipsRef = null;
    this.state = {
      generating: false,
      selectedMoodName: null,
      canScrollEnd: false,
    };
  }

  componentDidMount() {
    this.updateScrollFade();
    this.resizeObserver = new ResizeObserver(() => this.updateScrollFade());
    if (this.chipsRef) this.resizeObserver.observe(this.chipsRef);
    // The sheet now mounts already open, so there is no false→true update.
    if (this.props.open && !this.props.isProFeaturesLocked) {
      this.props.onOpen?.({ source: "section" });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open && this.chipsRef) {
      this.resizeObserver?.observe(this.chipsRef);
      this.updateScrollFade();
      if (!this.props.isProFeaturesLocked) {
        this.props.onOpen?.({ source: "section" });
      }
    }
    this.updateScrollFade();
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
    clearTimeout(this.closeAfterSelectTimer);
  }

  scheduleCloseAfterSelect() {
    clearTimeout(this.closeAfterSelectTimer);
    this.closeAfterSelectTimer = setTimeout(() => {
      this.setOpen(false);
    }, MOOD_SELECT_CLOSE_DELAY_MS);
  }

  updateScrollFade() {
    const el = this.chipsRef;
    if (!el) return;
    const canScrollEnd =
      el.scrollWidth > el.clientWidth + 1 &&
      el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    if (canScrollEnd !== this.state.canScrollEnd) {
      this.setState({ canScrollEnd });
    }
  }

  setOpen(open) {
    this.props.onOpenChange?.(open);
  }

  async runGenerate() {
    if (this.state.generating) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "generate" });
      this.props.onUpgradeClick?.();
      return;
    }
    this.setState({ generating: true });
    try {
      const mood = await this.props.onGenerateMood?.();
      if (mood?.name) await recordMoodUsed(mood.name);
      const popularNames = new Set(getPopularMoods().map((m) => m.name));
      this.setState({
        selectedMoodName:
          mood?.name && popularNames.has(mood.name) ? mood.name : null,
        generating: false,
      });
      this.setOpen(false);
    } catch {
      this.setState({ generating: false });
    }
  }

  async pickMood(mood) {
    if (this.props.isProFeaturesLocked) {
      this.props.onLockedClick?.({ source: "mood", mood: mood?.name });
      this.props.onUpgradeClick?.();
      return;
    }
    if (this.state.generating) return;

    if (this.state.selectedMoodName === mood.name) {
      this.setState({ generating: true, selectedMoodName: null });
      try {
        await this.props.onClearMood?.();
        this.setState({ generating: false });
        this.setOpen(false);
      } catch {
        this.setState({ generating: false });
      }
      return;
    }

    this.setState({ generating: true, selectedMoodName: mood.name });
    try {
      await this.props.onApplyMood?.(mood);
      await recordMoodUsed(mood.name);
      this.setState({ generating: false });
      this.scheduleCloseAfterSelect();
    } catch {
      this.setState({ generating: false });
    }
  }

  render() {
    const { open, isProFeaturesLocked } = this.props;
    const { generating, selectedMoodName, canScrollEnd } = this.state;
    const moods = getPopularMoods();
    const locked = !!isProFeaturesLocked;

    return (
      <div
        id="mood-sheet"
        className={`mood${open ? " mood--open" : ""}${
          generating ? " mood--busy" : ""
        }${locked ? " mood--locked" : ""}`}
        aria-label="Mood"
        aria-hidden={!open}
        {...(!open ? { inert: true } : {})}
      >
        <div className="mood__header">
          <div className="content__title">Mood</div>
          <p className="mood__subtitle">
            Pick a slowed mood or generate a fresh one.
          </p>
        </div>

        <div
          className={`mood__chips-wrap${canScrollEnd ? " mood__chips-wrap--fade-end" : ""}`}
        >
          <div
            className="mood__chips"
            ref={(el) => {
              this.chipsRef = el;
            }}
            onScroll={() => this.updateScrollFade()}
          >
            <button
              type="button"
              className={`mood__chip mood__chip--generate${locked ? " mood__chip--locked" : ""}`}
              onClick={() => this.runGenerate()}
              disabled={generating}
              aria-disabled={locked || undefined}
              tabIndex={open ? 0 : -1}
            >
              <SparkleIcon className="mood__generate-icon" />
              <span className="mood__chip-name">
                {generating ? "Slowing..." : "Generate"}
              </span>
            </button>
            {moods.map((mood) => {
              const isSelected = selectedMoodName === mood.name;
              return (
                <button
                  key={mood.name}
                  type="button"
                  className={`mood__chip${isSelected ? " mood__chip--selected" : ""}${locked ? " mood__chip--locked" : ""}`}
                  onClick={() => this.pickMood(mood)}
                  disabled={generating}
                  aria-disabled={locked || undefined}
                  title={mood.name}
                  aria-pressed={isSelected}
                  tabIndex={open ? 0 : -1}
                >
                  <span className="mood__chip-name">{mood.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
}
